import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  DestroyRef,
} from '@angular/core';
import {
  EditModeSelectionState,
  MaterialAnnotationType,
  MaterialDropdownState,
} from '../material-annotation-list.model';
import { HighlightIconComponent } from './highlight-icon/highlight-icon.component';
import { TranslateModule } from '@ngx-translate/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { StopPropagationDirective, TruncateDirective } from '@mhe/reader/common';
import { CommonModule } from '@angular/common';
import { ApiAnnotation } from '@mhe/reader/models';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { annotationTypeVal, MaterialAnnotationsListService } from '../material-annotations-list.service';
import { tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from '@mhe/ngx-shared';
import { TooltipModule } from '@mhe/ngx-shared/tooltip';
import { AriaAnnotationPipe } from '@mhe/reader/components/material-annotations-lists/utils/aria-annotation.pipe';
import { FilterByStatePipe } from '@mhe/reader/components/material-annotations-lists/utils/filter-by-state.pipe';
import { CleanTextPipe } from '@mhe/reader/components/material-annotations-lists/utils/clean-text.pipe';

@Component({
  selector: 'rdrx-material-annotations-list-content',
  templateUrl: './annotations-list-content.component.html',
  styleUrls: ['./annotations-list-content.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    TooltipModule,
    ButtonModule,
    StopPropagationDirective,
    TruncateDirective,
    MatCheckboxModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    HighlightIconComponent,
    FilterByStatePipe,
    AriaAnnotationPipe,
    CleanTextPipe,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialAnnotationListContentComponent implements OnInit {
  MaterialAnnotationType = MaterialAnnotationType;
  readonly annotationsListService = inject(MaterialAnnotationsListService);
  readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) type!: MaterialAnnotationType;
  @Input({ required: true }) isEditMode!: boolean;
  @Input({ required: true }) filterState!: MaterialDropdownState;

  @Output() spineIdExpanded = new EventEmitter<string>();
  @Output() annotationListItemClicked = new EventEmitter<ApiAnnotation>();
  @Output() annotationListSpineSelected = new EventEmitter<{ spineId: string, selected: boolean }>();
  @Output() annotationListResourceSelected = new EventEmitter<{
    spineId: string
    resourceUuid: string
    selected: boolean
  }>();

  @Output() editModeSelections = new EventEmitter<EditModeSelectionState>();
  @Output() deleteAnnotationListItem = new EventEmitter<ApiAnnotation>();
  @Output() hasAnnotations = new EventEmitter<boolean>();

  expandedSpines = signal<string[]>([]);
  annotations = signal<annotationTypeVal[]>([]);

  get emptyMessageKey(): string {
    switch (this.type) {
      case MaterialAnnotationType.HIGHLIGHTS:
        return 'contents.highlight_blank';
      case MaterialAnnotationType.NOTES:
        return 'contents.note_blank';
      case MaterialAnnotationType.PLACEMARKS:
        return 'contents.placemark_blank';
      default:
        return 'contents.empty';
    }
  }

  ngOnInit(): void {
    this.annotationsListService.annotationsListByType(this.type)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((annotations) => {
          // signal for ui update
          const hasAny = annotations.annotationsByChapter.some(ch => ch.annotations.length > 0);
          this.annotations.set(annotations.annotationsByChapter);
          this.hasAnnotations.emit(hasAny);

          // emit for edit mode selection checkbox state to the header
          const someSelected = annotations.annotations.some((annotation) => annotation.selected);
          const allSelected = annotations.annotations.every((annotation) => annotation.selected);
          this.editModeSelections.emit({ someSelected, allSelected });
        }),
      )
      .subscribe();
  }

  handleExpand(spineId: string): void {
    this.spineIdExpanded.emit(spineId);
  }

  handleAnnotationClicked(event: Event, annotation: ApiAnnotation): void {
    event.stopPropagation();
    event.preventDefault();
    this.annotationListItemClicked.emit(annotation);
  }

  handleAnnotationDelete(
    event: Event,
    annotation: ApiAnnotation,
  ): void {
    event.stopPropagation();
    event.preventDefault();
    this.deleteAnnotationListItem.emit(annotation);
  }

  handleSelectAllBySpineId(
    spineId: string,
    event: Event,
  ): void {
    event.stopPropagation();
    event.preventDefault();
    this.annotationListSpineSelected.emit({ spineId, selected: (event.target as HTMLInputElement)?.checked });
  }

  chapterAllSelected(chapter: annotationTypeVal): boolean {
    if (!chapter?.annotations.length) {
      return false;
    }

    return chapter?.annotations.every((annotation) => annotation.selected);
  }

  chapterPartiallySelected(chapter: annotationTypeVal): boolean {
    if (!chapter?.annotations.length) {
      return false;
    }

    return chapter?.annotations.some((annotation) => annotation.selected) &&
      !chapter?.annotations.every((annotation) => annotation.selected);
  }

  chapterToggleChildren(chapter: annotationTypeVal): void {
    const areSomeSelected = chapter.annotations.some((annotation) => annotation.selected);
    const areAllSelected = chapter.annotations.every((annotation) => annotation.selected);

    let toggleTo = false;

    if (areSomeSelected && !areAllSelected) {
      // some but not all
      toggleTo = true;
    } else if (!areSomeSelected && !areAllSelected) {
      // none
      toggleTo = true;
    }

    chapter.annotations.forEach((annotation) => {
      this.annotationListResourceSelected.emit({
        spineId: annotation.spineID,
        resourceUuid: annotation.resourceUUID,
        selected: toggleTo,
      });
    });
  }

  handleSelectAllByResource(
    annotation: ApiAnnotation,
  ): void {
    this.annotationListResourceSelected.emit({
      spineId: annotation.spineID,
      resourceUuid: annotation.resourceUUID,
      selected: !annotation.selected,
    });
  }

  handleExpandSpineId(expanded: boolean, spineId: string): void {
    if (expanded) {
      this.expandedSpines.update((spines) => [...spines, spineId]);
    } else {
      this.expandedSpines.update((spines) => spines.filter((id) => id !== spineId));
    }
  }

  isSpineExpanded(spineId: string): boolean {
    return this.expandedSpines().includes(spineId);
  }
}
